import { useQuery } from '@tanstack/react-query'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import clsx from 'clsx'
import { walletsApi, type WalletDetail as WalletDetailType } from '../utils/api'

function formatVolume(value: number): string {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`
  return `$${value.toFixed(0)}`
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

function getWinRateColor(rate: number): string {
  if (rate >= 0.6) return 'text-signal-green'
  if (rate >= 0.5) return 'text-signal-blue'
  if (rate >= 0.4) return 'text-star-muted'
  return 'text-signal-red'
}

// Strategy type labels
const strategyLabels: Record<string, string> = {
  favorite_backer: 'Favorite Backer',
  edge_trader: 'Edge Trader',
  market_maker: 'Market Maker',
  retail: 'Retail',
}

function getWalletTypeBadge(type: string) {
  const styles: Record<string, string> = {
    smart_money: 'bg-signal-green/20 text-signal-green border-signal-green/50',
    above_average: 'bg-signal-blue/20 text-signal-blue border-signal-blue/50',
    average: 'bg-star-dim/20 text-star-muted border-star-dim/50',
    below_average: 'bg-signal-red/20 text-signal-red border-signal-red/50',
    whale: 'bg-signal-amber/20 text-signal-amber border-signal-amber/50',
  }
  const labels: Record<string, string> = {
    smart_money: 'Smart Money',
    above_average: 'Above Average',
    average: 'Average',
    below_average: 'Below Average',
    whale: 'Whale',
  }
  return (
    <span className={clsx('px-3 py-1 rounded-full border text-sm font-medium', styles[type] || styles.average)}>
      {labels[type] || type}
    </span>
  )
}

function getStrategyBadge(strategy: string | null) {
  if (!strategy) return null

  const styles: Record<string, string> = {
    favorite_backer: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
    edge_trader: 'bg-signal-green/20 text-signal-green border-signal-green/50',
    market_maker: 'bg-signal-blue/20 text-signal-blue border-signal-blue/50',
    retail: 'bg-star-dim/20 text-star-muted border-star-dim/50',
  }

  return (
    <span className={clsx('px-3 py-1 rounded-full border text-sm font-medium', styles[strategy] || styles.retail)}>
      {strategyLabels[strategy] || strategy}
    </span>
  )
}

function StatCard({ label, value, subValue }: {
  label: string
  value: string | number
  subValue?: string
}) {
  return (
    <div className="glass-card p-4">
      <div className="text-xs text-star-muted mb-1">{label}</div>
      <div className="text-2xl font-mono font-semibold text-star">
        {value}
      </div>
      {subValue && <div className="text-xs text-star-dim mt-1">{subValue}</div>}
    </div>
  )
}

function CategoryBreakdown({ categories }: { categories: WalletDetailType['categories'] }) {
  if (!categories || categories.length === 0) {
    return (
      <div className="glass-card p-4">
        <h3 className="text-sm font-medium text-star mb-3">Category Breakdown</h3>
        <p className="text-star-muted text-sm">No resolved trades yet</p>
      </div>
    )
  }

  const totalVolume = categories.reduce((sum, c) => sum + c.volume_usd, 0)

  return (
    <div className="glass-card p-4">
      <h3 className="text-sm font-medium text-star mb-4">Category Breakdown</h3>

      {/* Visual bar */}
      <div className="h-6 flex rounded-lg overflow-hidden mb-4">
        {categories.map((cat, i) => {
          const pct = (cat.volume_usd / Math.max(totalVolume, 1)) * 100
          const colors = [
            'bg-signal-blue',
            'bg-signal-green',
            'bg-signal-amber',
            'bg-purple-500',
            'bg-pink-500',
            'bg-star-dim',
          ]
          return (
            <div
              key={cat.category}
              className={clsx('flex items-center justify-center text-xs font-medium text-void-300', colors[i % colors.length])}
              style={{ width: `${pct}%` }}
              title={`${cat.category}: ${pct.toFixed(1)}%`}
            >
              {pct >= 15 ? cat.category : ''}
            </div>
          )
        })}
      </div>

      {/* Table */}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-star-dim text-xs border-b border-star-dim/20">
            <th className="text-left pb-2">Category</th>
            <th className="text-right pb-2">Win Rate</th>
            <th className="text-right pb-2">Record</th>
            <th className="text-right pb-2">Volume</th>
          </tr>
        </thead>
        <tbody>
          {categories.map((cat) => (
            <tr key={cat.category} className="border-b border-star-dim/10 last:border-0">
              <td className="py-2 text-star">{cat.category}</td>
              <td className={clsx('py-2 text-right font-mono', getWinRateColor(cat.win_rate / 100))}>
                {cat.win_rate.toFixed(1)}%
              </td>
              <td className="py-2 text-right text-star-muted">
                <span className="text-signal-green">{cat.wins}</span>
                <span className="text-star-dim">/</span>
                <span>{cat.markets}</span>
              </td>
              <td className="py-2 text-right text-star-muted font-mono">
                {formatVolume(cat.volume_usd)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function PerformanceGauge({ winRate, confidence }: { winRate: number; confidence: number }) {
  // Win rate is 0-1, confidence is 0-1
  const gaugeWidth = Math.min(winRate * 100, 100)

  return (
    <div className="glass-card p-4">
      <h3 className="text-sm font-medium text-star mb-4">Performance</h3>

      <div className="space-y-4">
        {/* Win Rate Gauge */}
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-star-muted">Win Rate</span>
            <span className={clsx('font-mono font-semibold', getWinRateColor(winRate))}>
              {formatPercent(winRate)}
            </span>
          </div>
          <div className="h-3 bg-void-200 rounded-full overflow-hidden">
            <div
              className={clsx(
                'h-full rounded-full transition-all duration-500',
                winRate >= 0.6 ? 'bg-signal-green' :
                winRate >= 0.5 ? 'bg-signal-blue' :
                winRate >= 0.4 ? 'bg-signal-amber' : 'bg-signal-red'
              )}
              style={{ width: `${gaugeWidth}%` }}
            />
          </div>
          <div className="flex justify-between text-xs mt-1 text-star-dim">
            <span>0%</span>
            <span className="text-star-muted">50%</span>
            <span>100%</span>
          </div>
        </div>

        {/* Confidence */}
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-star-muted">Confidence</span>
            <span className="font-mono text-star">
              {formatPercent(confidence)}
            </span>
          </div>
          <div className="h-2 bg-void-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-signal-blue rounded-full transition-all duration-500"
              style={{ width: `${confidence * 100}%` }}
            />
          </div>
          <p className="text-xs text-star-dim mt-1">
            Based on number of resolved trades
          </p>
        </div>
      </div>
    </div>
  )
}

export function WalletDetail() {
  const { address } = useParams<{ address: string }>()

  const { data, isLoading, error } = useQuery({
    queryKey: ['wallet-detail', address],
    queryFn: () => walletsApi.detail(address!),
    enabled: !!address,
  })

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 rounded-full border-2 border-signal-blue/30 border-t-signal-blue animate-spin" />
            <p className="text-star-muted font-mono text-sm">Loading wallet data...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="glass-card p-8 text-center">
          <p className="text-signal-red font-mono mb-4">Wallet not found</p>
          <Link to="/wallets" className="btn-primary">
            Back to Wallets
          </Link>
        </div>
      </div>
    )
  }

  const losses = data.resolved_trades - data.winning_trades

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <div className="flex items-center gap-2 mb-4">
          <Link to="/wallets" className="text-star-muted hover:text-star transition-colors">
            Wallets
          </Link>
          <span className="text-star-dim">/</span>
          <span className="text-star font-mono text-sm">{data.address.slice(0, 10)}...{data.address.slice(-6)}</span>
        </div>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-display font-semibold text-star font-mono">
                {data.address.slice(0, 10)}...{data.address.slice(-6)}
              </h1>
              {getWalletTypeBadge(data.type)}
              {getStrategyBadge(data.strategy_type)}
            </div>
            <div className="flex items-center gap-4 text-sm text-star-muted">
              <a
                href={`https://polygonscan.com/address/${data.address}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-signal-blue transition-colors"
              >
                View on PolygonScan
              </a>
              <a
                href={`https://polymarket.com/profile/${data.address}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-signal-blue transition-colors"
              >
                View on Polymarket
              </a>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4 mb-6"
      >
        <StatCard
          label="Win Rate"
          value={formatPercent(data.win_rate)}
          subValue={`${data.winning_trades}-${losses} (${data.resolved_trades} resolved)`}
        />
        <StatCard
          label="Est. Profit"
          value={`${data.estimated_pnl >= 0 ? '+' : ''}${formatVolume(data.estimated_pnl)}`}
          subValue={`${data.roi_pct >= 0 ? '+' : ''}${data.roi_pct.toFixed(1)}% ROI`}
        />
        <StatCard
          label="Expectancy"
          value={data.expectancy != null ? `$${data.expectancy.toFixed(0)}` : 'N/A'}
          subValue="per $100 wagered"
        />
        <StatCard
          label="Profit Factor"
          value={data.profit_factor != null ? data.profit_factor.toFixed(2) : 'N/A'}
          subValue={data.profit_factor != null && data.profit_factor > 1 ? 'Winning strategy' : 'Losing strategy'}
        />
      </motion.div>

      {/* Second Stats Row */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4 mb-6"
      >
        <StatCard
          label="Total Volume"
          value={formatVolume(data.total_volume_usd)}
          subValue={`${data.trade_count} trades`}
        />
        <StatCard
          label="Resolved Volume"
          value={formatVolume(data.resolved_volume)}
        />
        <StatCard
          label="Unique Markets"
          value={data.unique_markets}
        />
        <StatCard
          label="Best Category"
          value={data.best_category || 'N/A'}
          subValue={data.worst_category ? `Worst: ${data.worst_category}` : undefined}
        />
      </motion.div>

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <PerformanceGauge winRate={data.win_rate} confidence={data.confidence} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <CategoryBreakdown categories={data.categories} />
        </motion.div>
      </div>

      {/* Updated timestamp */}
      {data.updated_at && (
        <div className="text-center text-xs text-star-dim">
          Last updated: {new Date(data.updated_at).toLocaleString()}
        </div>
      )}
    </div>
  )
}
